import isEqual from 'lodash/isEqual'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

import ErrorBoundary from '../containers/error_boundary'
import {
  AppLocation,
  AppLocationCacheWithMetadata,
  appLocationCacheInitialState,
  emptyObject,
} from '../pages/admin/app_location_provider'
import { LAZY_WIDGET_MEMBER_ID } from '../pages/live_chat/constants'
import { setCurrent } from '../reduxes/slices/current/actions'
import {
  getHandlesIndexedByMemberId,
  getMemberIds,
} from '../reduxes/slices/members/selectors'
import { setSelected } from '../reduxes/slices/selected/actions'
import {
  useDispatchDelegateStore,
  useSelectorDelegateStore,
} from '../reduxes/stores/root_store/hooks'
import usePreviousProps from './previous_props_hook'
import { useLocalStorage } from './use_local_storage'

type EmptyRecord = Record<string, never>

type ReturnValue = {
  appLocation: AppLocation | EmptyRecord
  appLocationCache: AppLocationCacheWithMetadata
  setAppLocationCache: React.Dispatch<
    React.SetStateAction<AppLocationCacheWithMetadata>
  >
  prevAppLocation: AppLocation | EmptyRecord
}

export const APP_LOCATION_CACHE_LS_KEY = 'app_location_cache'

const hasLocationChanged = (
  appLocation: AppLocation,
  prevAppLocation: AppLocation | EmptyRecord,
) => {
  return !isEqual(appLocation, prevAppLocation)
}

export const useAppLocationStateManager = (dispatches: any): ReturnValue => {
  const [initialized, setInitialized] = useState(false)
  const ls = useLocalStorage()
  const [appLocationCache, setAppLocationCache] =
    useState<AppLocationCacheWithMetadata>(() => {
      const result: string | null = ls.getItem(APP_LOCATION_CACHE_LS_KEY)
      return result || appLocationCacheInitialState
    })
  const appLocationCachePrevRender =
    usePreviousProps<AppLocationCacheWithMetadata>(appLocationCache)
  const [appLocationTicked, setAppLocationTicked] = useState<
    AppLocation | EmptyRecord
  >(emptyObject)
  const [[prevAppLocation], setPrevAppLocation] = useState<
    (AppLocation | EmptyRecord)[]
  >([emptyObject, emptyObject])

  const handleLookup = useSelectorDelegateStore(getHandlesIndexedByMemberId)
  const memberIds = useSelectorDelegateStore(getMemberIds)
  const dispatchDelegateStore = useDispatchDelegateStore()
  const navigate = useNavigate()

  useEffect(() => {
    ls.setItem(APP_LOCATION_CACHE_LS_KEY, appLocationCache)
    setAppLocationTicked((prevState) => {
      if (!appLocationCache || appLocationCache.lastSetMemberId === undefined) {
        return prevState
      }
      const location = appLocationCache.cache[appLocationCache.lastSetMemberId]
      if (hasLocationChanged(location, prevState)) {
        return location
      } else {
        return prevState
      }
    })
  }, [appLocationCache, dispatches, ls])

  // responsible for setting the previous app location
  useEffect(() => {
    if (!appLocationCache.lastSetMemberId) {
      return
    }
    const appLocation = appLocationCache.cache[appLocationCache.lastSetMemberId]
    setPrevAppLocation((prevState) => {
      const prevLocation = prevState[1]
      if (hasLocationChanged(appLocation, prevLocation)) {
        return prevState.slice(1).concat(appLocation)
      }
      return prevState
    })
  }, [appLocationCache])

  const performSideEffects = useCallback(
    (locationChanges, prevLocation) => {
      if (!initialized) {
        setInitialized(true)
      }
      let memberId = locationChanges.memberId
      const { url } = locationChanges
      if (!memberId) {
        ErrorBoundary.logErrorToMyService(
          new Error('memberId is undefined in AppLocationProvider'),
        )
        return false
      }
      let dispatch = dispatches[memberId]
      if (memberId === LAZY_WIDGET_MEMBER_ID && !dispatch) {
        memberId = Object.keys(dispatches)[0]
        dispatch = dispatches[memberId]
      }

      const { memberId: lastSetMemberId, url: prevUrl } = prevLocation || {}

      if (memberId && (memberId !== lastSetMemberId || !initialized)) {
        const handle = handleLookup[memberId]
        dispatchDelegateStore(
          setCurrent({
            memberId,
            handle,
          }),
        )
      }
      if (url && (url !== prevUrl || !initialized)) {
        navigate(url)
      }
      dispatch(setSelected(locationChanges))
    },
    [initialized, dispatches, handleLookup, dispatchDelegateStore, navigate],
  )

  // react to the app location changing and perform all side effects
  useEffect(() => {
    if (
      !Object.keys(dispatches).length ||
      !appLocationCache.lastSetMemberId ||
      (initialized && appLocationCache === appLocationCachePrevRender)
    ) {
      return
    }

    const validMemberId =
      memberIds.includes(appLocationCache.lastSetMemberId) ||
      appLocationCache.lastSetMemberId === LAZY_WIDGET_MEMBER_ID
    if (!validMemberId) return // Dont use invalid member cache
    const location = appLocationCache.cache[appLocationCache.lastSetMemberId]

    const prevLocation =
      appLocationCachePrevRender &&
      appLocationCachePrevRender.lastSetMemberId &&
      appLocationCachePrevRender.cache[
        appLocationCachePrevRender.lastSetMemberId
      ]

    performSideEffects(location, prevLocation)
  }, [
    appLocationCache,
    performSideEffects,
    appLocationCachePrevRender,
    dispatches,
    initialized,
    memberIds,
  ])

  return {
    // tick state to the next render, so that side effects are available in the same render
    appLocation: appLocationTicked,
    // appLocationCache is NOT ticked to next render
    appLocationCache,
    setAppLocationCache,
    prevAppLocation,
  }
}
